import { CheckoutUpsellImageAspectRatio } from "@redotech/congo-enums/checkout-upsell-image-aspect-ratio";
import { Currency } from "@redotech/money/currencies";
import { CheckoutWidget } from "@redotech/redo-conversion-core-common/checkouts/checkout-widget";
import { CheckoutWidgetsBuilderState } from "@redotech/redo-conversion-core-common/checkouts/checkout-widgets-builder";
import {
  CheckoutOptimizationWidgetType,
  LOW_PROFILE_PRODUCT_UPSELL_MAX_ITEMS,
  ManualProductSourceType,
} from "@redotech/redo-conversion-core-common/checkouts/widget";
import { safeParseEnum } from "@redotech/util/enum";
import { assertNever } from "@redotech/util/type";

/**
 * Converts a persisted {@link CheckoutWidget} (the DB/RPC shape returned by
 * `getCheckoutWidgets`) into the builder-state entry the widget preview and
 * settings panels read. The DB and builder shapes diverge for several widget
 * types (e.g. threshold upsell stores `threshold.amount` / structured
 * `progressBarColor`, while the builder uses `thresholdAmount` / a flat color),
 * so feeding a raw widget straight into the builder context renders incorrectly.
 *
 * Shared by the widget editor and the checkout-tree node hover preview so both
 * go through one authoritative mapping.
 */
export function checkoutWidgetToBuilderState(
  existingWidget: CheckoutWidget,
  defaultCurrency: Currency,
): Partial<CheckoutWidgetsBuilderState["checkoutWidgets"]> {
  const widgetType = existingWidget.type;
  switch (widgetType) {
    case CheckoutOptimizationWidgetType.THRESHOLD_UPSELL:
      return {
        [widgetType]: {
          type: widgetType,
          id: existingWidget._id,
          name: existingWidget.name,
          enabled: true,
          defaultEnabled: existingWidget.defaultEnabled,
          target: existingWidget.target,
          thresholdAmount: existingWidget.threshold.amount,
          currency:
            safeParseEnum(existingWidget.threshold.currency, Currency) ??
            defaultCurrency,
          gteThresholdText: existingWidget.gteThresholdText,
          ltThresholdText: existingWidget.ltThresholdText,
          addToCartButtonColor:
            existingWidget.addToCartButtonColor ?? "#141414",
          addToCartButtonTextColor:
            existingWidget.addToCartButtonTextColor ?? "#FFFFFF",
          progressBarColor:
            existingWidget.progressBarColor?.filled ?? "#141414",
          excludedProductTags: existingWidget.excludedProductTags ?? [],
          fallbackProductTags: existingWidget.fallbackProductTags ?? [],
          manualProductSources: existingWidget.manualProductSources ?? [],
          defaultProductDiscount: existingWidget.defaultProductDiscount,
          addToCartButtonText: existingWidget.addToCartButtonText,
          styling: {
            imageAspectRatio:
              existingWidget.styling?.imageAspectRatio ??
              CheckoutUpsellImageAspectRatio.Square,
            squareImageBackdropCorners:
              existingWidget.styling?.squareImageBackdropCorners ?? false,
            squareButtonCorners:
              existingWidget.styling?.squareButtonCorners ?? false,
            plainProductTitle:
              existingWidget.styling?.plainProductTitle ?? false,
            hideZeroMinorUnits:
              existingWidget.styling?.hideZeroMinorUnits ?? false,
          },
        },
      };
    case CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL:
      return {
        [widgetType]: {
          type: widgetType,
          id: existingWidget._id,
          name: existingWidget.name,
          enabled: true,
          defaultEnabled: existingWidget.defaultEnabled,
          target: existingWidget.target,
          addToCartButtonColor:
            existingWidget.addToCartButtonColor ?? "#141414",
          addToCartButtonTextColor:
            existingWidget.addToCartButtonTextColor ?? "#FFFFFF",
          excludedProductTags: existingWidget.excludedProductTags ?? [],
          fallbackProductTags: existingWidget.fallbackProductTags ?? [],
          manualProductSources: (() => {
            const manualProductSources = (
              existingWidget.manualProductSources ?? []
            ).slice(0, LOW_PROFILE_PRODUCT_UPSELL_MAX_ITEMS);

            return manualProductSources.length > 0
              ? manualProductSources
              : [{ type: ManualProductSourceType.REDO_AI }];
          })(),
        },
      };
    case CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF:
      return {
        [widgetType]: {
          type: widgetType,
          id: existingWidget._id,
          name: existingWidget.name,
          enabled: true,
          defaultEnabled: existingWidget.defaultEnabled,
          target: existingWidget.target,
          text: existingWidget.text,
          backgroundColor: existingWidget.backgroundColor,
          iconColor: existingWidget.iconColor,
        },
      };
    case CheckoutOptimizationWidgetType.CONTENT_BLOCK:
      return {
        [widgetType]: {
          type: widgetType,
          id: existingWidget._id,
          name: existingWidget.name,
          enabled: true,
          defaultEnabled: existingWidget.defaultEnabled,
          target: existingWidget.target,
          sections: existingWidget.sections ?? [],
        },
      };
    case CheckoutOptimizationWidgetType.IMAGE_BLOCK:
      return {
        [widgetType]: {
          type: widgetType,
          id: existingWidget._id,
          name: existingWidget.name,
          enabled: true,
          defaultEnabled: existingWidget.defaultEnabled,
          target: existingWidget.target,
          imageUrl: existingWidget.imageUrl,
          imageWidthPx: existingWidget.imageWidthPx,
          imageJustification: existingWidget.imageJustification,
        },
      };
    case CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT:
      return {
        [widgetType]: {
          type: widgetType,
          id: existingWidget._id,
          name: existingWidget.name,
          enabled: true,
          target: existingWidget.target,
          title: existingWidget.title ?? "",
          description: existingWidget.description ?? "",
          imageUrl: existingWidget.imageUrl,
          backgroundColor: existingWidget.backgroundColor,
        },
      };
    case CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE:
      return {
        [widgetType]: {
          type: widgetType,
          id: existingWidget._id,
          name: existingWidget.name,
          enabled: true,
          target: existingWidget.target,
          imageUrl: existingWidget.imageUrl,
          imageWidth: existingWidget.imageWidth,
          imageJustification: existingWidget.imageJustification,
        },
      };
    case CheckoutOptimizationWidgetType.SHIPPING_OPTION_PRODUCT_UPSELL:
      return {
        [widgetType]: {
          type: widgetType,
          id: existingWidget._id,
          name: existingWidget.name,
          enabled: true,
          target: existingWidget.target,
          addToCartButtonText: existingWidget.addToCartButtonText,
          excludedProductTags: existingWidget.excludedProductTags ?? [],
          fallbackProductTags: existingWidget.fallbackProductTags ?? [],
          manualProductSources: (() => {
            const manualProductSources = (
              existingWidget.manualProductSources ?? []
            ).slice(0, LOW_PROFILE_PRODUCT_UPSELL_MAX_ITEMS);

            return manualProductSources.length > 0
              ? manualProductSources
              : [{ type: ManualProductSourceType.REDO_AI }];
          })(),
          defaultProductDiscount: existingWidget.defaultProductDiscount,
          progressBar: existingWidget.progressBar
            ? {
                thresholdAmount: existingWidget.progressBar.threshold.amount,
                currency:
                  safeParseEnum(
                    existingWidget.progressBar.threshold.currency,
                    Currency,
                  ) ?? defaultCurrency,
                gteThresholdText: existingWidget.progressBar.gteThresholdText,
                ltThresholdText: existingWidget.progressBar.ltThresholdText,
              }
            : null,
        },
      };
    case CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE:
      return {
        [widgetType]: {
          type: widgetType,
          id: existingWidget._id,
          name: existingWidget.name,
          enabled: true,
          defaultEnabled: existingWidget.defaultEnabled,
          target: existingWidget.target,
          discountId: existingWidget.discountId,
          text: existingWidget.text,
          buttonText: existingWidget.buttonText,
        },
      };
    case CheckoutOptimizationWidgetType.PRODUCT_REVIEWS:
      return {
        [widgetType]: {
          type: widgetType,
          id: existingWidget._id,
          name: existingWidget.name,
          enabled: true,
          defaultEnabled: existingWidget.defaultEnabled,
          target: existingWidget.target,
          provider: existingWidget.provider,
          minRating: existingWidget.minRating,
          display: existingWidget.display,
        },
      };
    default:
      return assertNever(widgetType);
  }
}
